import { RouteComponentProps, Router } from '@gatsbyjs/reach-router'
import React, { useEffect } from 'react'
import styled from 'styled-components'

import {
  cleanupTaskOnSuccess$,
  latestPlaceOrderTask$,
  sensitiveTaskResetClock$,
  sensitiveTaskResetOnTimeout$,
} from '../../../lib/cart/state/order'
import { getRouterBasePath, useDispensaryContext } from '../../../lib/dispensaries'
import { EmptyObject } from '../../../utils/types'
import { CartPage } from '../cart/CartPage'
import { ConfirmationMarketingContentProps } from '../confirmation/ConfirmationMarketingContent'
import { ConfirmationPage } from '../confirmation/ConfirmationPage'
import { AgreementsPage } from './AgreementsPage'
import { CheckoutOnboardingPage } from './CheckoutOnboardingPage'
import { DeliveryPage } from './DeliveryPage'
import { PaymentPage } from './PaymentPage'
import { ReviewPage } from './ReviewPage'
import { ShippingDetailsPage } from './ShippingDetailsPage'

const StyledRouter = styled(Router)`
  // expand to fill focus wrapper added by Gatsby
  // ref: https://github.com/gatsbyjs/gatsby/issues/7310
  width: 100%;
  height: 100%;
`

type CheckoutRouteProps<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TComponent extends React.ComponentType<any>,
  TParamProps extends Exclude<keyof React.ComponentProps<TComponent>, 'children'> = never,
> = RouteComponentProps<EmptyObject> &
  Omit<React.ComponentProps<TComponent>, TParamProps> & {
    component: TComponent
    paramProps?: ReadonlyArray<TParamProps>
  }

const CheckoutRoute = <
  // Note: `any` seems to be required for inference - may be related to bivariance
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TComponent extends React.ComponentType<any>,
  TParamProps extends Exclude<keyof React.ComponentProps<TComponent>, 'children'> = never,
>({
  component,
  path,
  paramProps: _paramProps,
  ...componentProps
}: CheckoutRouteProps<TComponent, TParamProps>): React.ReactElement => {
  return React.createElement(component, componentProps)
}

export type CheckoutRoutesProps = ConfirmationMarketingContentProps

export const CheckoutRoutes: React.FC<CheckoutRoutesProps> = ({ checkoutMarketingContent }) => {
  useEffect(() => {
    const subscriptions = [
      // hold subscription to place order task populated by `/payment` and used on `/review` routes
      latestPlaceOrderTask$.subscribe(),
      sensitiveTaskResetClock$.subscribe(),
      sensitiveTaskResetOnTimeout$.subscribe(),
      cleanupTaskOnSuccess$.subscribe(),
    ]
    return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
  }, [])

  const basePath = getRouterBasePath('/checkout', !!useDispensaryContext())

  return (
    <StyledRouter basepath={basePath}>
      <CheckoutRoute path="/" component={CartPage} />
      <CheckoutRoute path="/agreements" component={AgreementsPage} />
      <CheckoutRoute
        path="/confirmation/*"
        component={ConfirmationPage}
        checkoutMarketingContent={checkoutMarketingContent}
      />
      <CheckoutRoute path="/delivery" component={DeliveryPage} />
      <CheckoutRoute path="/onboarding" component={CheckoutOnboardingPage} />
      <CheckoutRoute path="/payment" component={PaymentPage} />
      <CheckoutRoute path="/review" component={ReviewPage} />
      <CheckoutRoute path="/shipping" component={ShippingDetailsPage} />
    </StyledRouter>
  )
}
